<!-- src/views/LoginPage.vue -->
<template>
  <div class="page-background">
    <div class="layout-container">
      <div class="info-container">
        <img src="../assets/MN logo.png" alt="MN Logo" class="mn-logo" />
        <h2>Match Reports</h2>
        <p class="MR-description">
          Match Reports is a streamlined platform that collects and stores match scores, misconduct, game comments, and behavior assessments...
        </p>
        <p class="copywrite">2022-25 Protected by Miller Newlon. All Rights Reserved.</p>
      </div>

      <div class="form-container">
        <h1>Login</h1>

        <div v-if="loginError" class="error-message">{{ loginError }}</div>

        <form @submit.prevent="handleLogin">
          <!-- Email / Password -->
          <input 
            v-model="email" 
            type="email" 
            placeholder="Email" 
            required 
            autocomplete="username"
            :disabled="loading" 
          />
          <input 
            v-model="password" 
            type="password" 
            placeholder="Password" 
            required 
            autocomplete="current-password"
            :disabled="loading"
            @keyup.enter="handleLogin"
          />

          <!-- Organization: simplified to just LPSRA -->
          <v-select label="Organization" v-model="selectedOrg"
            :items="orgOptions" item-title="name" item-value="value"
            :disabled="loading"
            clearable
            required
            variant="outlined"
            prepend-inner-icon="mdi-office-building"
          />

          <!-- Role: simplified to just the three roles -->
          <v-select
            label="Select Your Role"
            v-model="selectedRole"
            :items="availableRoles"
            item-title="text"   
            item-value="value" 
            :disabled="loading || !selectedOrg"
            clearable
            required
            variant="outlined"
            prepend-inner-icon="mdi-account"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-icon :color="item.raw.color" class="mr-3">
                    {{ item.raw.icon }}
                  </v-icon>
                </template>
              </v-list-item>
            </template>
            
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-icon :color="item.raw.color" size="small" class="mr-2">
                  {{ item.raw.icon }}
                </v-icon>
                {{ item.raw.text }}
              </div>
            </template>
          </v-select>

          <div class="checkbox_rememberme">
            <input type="checkbox" v-model="rememberMe" id="rememberMe" />
            <label for="rememberMe">Remember my login credentials</label>
          </div>

          <button type="submit" :disabled="loginLoading || !formValid">
            <span v-if="loginLoading" class="spinner"></span> 
            {{ loginLoading ? 'Signing In...' : 'Login' }}
          </button>
          <div v-if="loginLoading">Authenticating...</div>
        </form>

        <p class="newuser">
          NEW USERs <router-link to="/signup">Sign up</router-link> HERE
        </p>

        <p class="forgotpassword">
          Forgot password? <a href="#" @click.prevent="openReset">Reset it here</a>
        </p>

        <v-dialog v-model="resetOpen" max-width="420">
          <v-card>
            <v-card-title>Reset password</v-card-title>
            <v-card-text>
              <v-text-field 
                v-model="resetEmail" 
                label="Email" 
                type="email" 
                variant="outlined"
                required 
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="resetOpen=false">Cancel</v-btn>
              <v-btn color="primary" @click="sendReset">Send</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, functions } from "../firebase";

// Reactive refs
const router = useRouter()
const loginLoading = ref(false)
const loading = ref(false)
const email = ref(localStorage.getItem("email") || "");
const password = ref(localStorage.getItem("rememberedPassword") || "");
const selectedOrg = ref(localStorage.getItem("currentUserOrgName") || "LPSRA");
const selectedRole = ref(localStorage.getItem("currentUserRole") || "Referee");
const rememberMe = ref(true);
const loginError = ref("");
const resetOpen = ref(false);
const resetEmail = ref("");
const emailNotVerified = ref(false);

// User claims for role filtering
const userClaims = ref({})

// Organization options
const orgOptions = ref([ { name: "LPSRA", value: "LPSRA" } ]);

// Available roles - filtered by user claims
const availableRoles = computed(() => {
  const allRoles = [
    { text: "Referee", value: "Referee", icon: "mdi-whistle", color: "primary" },
    { text: "Referee Coach", value: "RefereeCoach", icon: "mdi-account-star", color: "success" },
    { text: "Administrator", value: "ADMIN", icon: "mdi-shield-account", color: "error" }
  ]
  
  // If no claims yet, show all roles (for initial display)
  if (Object.keys(userClaims.value).length === 0) {
    return allRoles
  }
  
  // Filter roles based on user claims from multiple possible locations
  return allRoles.filter(role => {
    // Check direct claims
    if (userClaims.value[role.value] === true) return true
    
    // Check single role property
    if (userClaims.value.role && userClaims.value.role === role.value) return true
    
    // Check organization-based roles object
    // Check NEW orgRoles structure (org -> array of roles)
      if (userClaims.value.orgRoles && typeof userClaims.value.orgRoles === 'object') {
        const orgRoleArrays = Object.values(userClaims.value.orgRoles)
        for (const roleArray of orgRoleArrays) {
          if (Array.isArray(roleArray) && roleArray.includes(role.value)) return true
        }
      }
      
      // Check OLD organization-based roles object
      if (userClaims.value.roles && typeof userClaims.value.roles === 'object' && !Array.isArray(userClaims.value.roles)) {
      const orgRoles = Object.values(userClaims.value.roles)
      if (orgRoles.includes(role.value)) return true
    }
    
    // Check custom claims
    if (userClaims.value.custom && userClaims.value.custom[role.value] === true) return true
    
    // Check roles array
    if (userClaims.value.roles && Array.isArray(userClaims.value.roles) && userClaims.value.roles.includes(role.value)) return true
    
    return false
  })
})

// Form validation
const formValid = computed(() => {
  return email.value && 
         password.value && 
         selectedOrg.value && 
         selectedRole.value &&
         email.value.includes("@") &&
         password.value.length >= 6
})

// Functions
function openReset() {
  resetOpen.value = true;
  resetEmail.value = email.value || "";
}

async function sendReset() {
  try {
    await sendPasswordResetEmail(auth, resetEmail.value);
    loginError.value = "Reset email sent.";
    resetOpen.value = false;
  } catch (e) {
    loginError.value = e?.message || "Failed to send reset email.";
  }
}

async function resendVerificationEmail() {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user.");
    await sendEmailVerification(user);
    loginError.value = "Verification email sent.";
  } catch (e) {
    loginError.value = e?.message || "Failed to send verification email.";
  }
}

// Function to check user roles when email is entered
const checkUserRoles = async () => {
  if (!email.value || !email.value.includes('@')) {
    userClaims.value = {}
    return
  }
  
  try {
    console.log(`🔍 Checking roles for email: ${email.value}`)
    // For security, we can't check claims without authentication
    // So we'll clear the claims and let the login process handle role verification
    userClaims.value = {}
    console.log('ℹ️ Role claims will be verified during login process')
  } catch (error) {
    console.log('Role check failed:', error)
    userClaims.value = {}
  }
}

const handleLogin = async () => {
  // Validate form first
  if (!formValid.value) {
    if (!email.value) loginError.value = "Please enter your email address."
    else if (!password.value) loginError.value = "Please enter your password."
    else if (!selectedOrg.value) loginError.value = "Please select an organization."
    else if (!selectedRole.value) loginError.value = "Please select a role."
    return
  }
  
  loginLoading.value = true
  loginError.value = ""
  
  try {
    console.log(`== Login initiated for ${email.value}, Org: ${selectedOrg.value}, Role: ${selectedRole.value} == `)
    
    // Step 1: Firebase authentication
    const userCredential = await signInWithEmailAndPassword(auth, email.value, password.value)
    const user = userCredential.user
    console.log('✅ 1... Firebase auth successful:', user.uid)
    
    // Step 2: Get fresh token and user claims
    const idTokenResult = await user.getIdTokenResult(true)
    console.log('📋 2... Reading Token and User claims:', idTokenResult.claims)
    
    // Log which claims structure we're using
    // Will log after claimsFromToken is declared below
    userClaims.value = idTokenResult.claims         // Update userClaims reactive ref for UI updates
    
    // Step 3: Role verification
    console.log(`3... Started Verification process for selected login role ${selectedRole.value}`)
    const claimsFromToken = idTokenResult.claims
    
    // Log which claims structure we're using
    if (claimsFromToken.orgRoles) {
      console.log("🆕 Using NEW orgRoles structure:", claimsFromToken.orgRoles)
    }
    if (claimsFromToken.roles && typeof claimsFromToken.roles === 'object' && !Array.isArray(claimsFromToken.roles)) {
      console.log("🔄 Using OLD roles object structure:", claimsFromToken.roles)
    }
    if (claimsFromToken.roles && Array.isArray(claimsFromToken.roles)) {
      console.log("📝 Using roles array structure:", claimsFromToken.roles)
    }
    const userRoles = []          // Extract ALL user roles (store as array)
    const validRoles = ['Referee', 'RefereeCoach', 'ADMIN']
    
    // Check direct claims (individual role properties)
    validRoles.forEach(role => {
      if (claimsFromToken[role] === true) {
        userRoles.push(role)
        console.log(`✅ Found role in direct claims: ${role}`)
      }
    })
    
    /*
    // Check single role property
    if (claimsFromToken.role && validRoles.includes(claimsFromToken.role)) {
      if (!userRoles.includes(claimsFromToken.role)) {
        userRoles.push(claimsFromToken.role)
        console.log(`✅ Found role in single role property: ${claimsFromToken.role}`)
      }
    }
    */
    // Check NEW orgRoles structure (org -> array of roles)
       if (claimsFromToken.orgRoles && typeof claimsFromToken.orgRoles === 'object') {
         Object.keys(claimsFromToken.orgRoles).forEach(org => {
           const rolesForOrg = claimsFromToken.orgRoles[org]
           if (Array.isArray(rolesForOrg)) {
             rolesForOrg.forEach(role => {
               if (validRoles.includes(role) && !userRoles.includes(role)) {
                 userRoles.push(role)
                 console.log(`✅ Found role in orgRoles (${org}): ${role}`)
               }
             })
           }
         })
       }
       
       // Check OLD organization-based roles object (roles.LPSRA = "Referee")
    if (claimsFromToken.roles && typeof claimsFromToken.roles === 'object' && !Array.isArray(claimsFromToken.roles)) {
      Object.keys(claimsFromToken.roles).forEach(org => {
        const roleForOrg = claimsFromToken.roles[org]
        if (validRoles.includes(roleForOrg) && !userRoles.includes(roleForOrg)) {
          userRoles.push(roleForOrg)
          console.log(`✅ Found role in organization roles (${org}): ${roleForOrg}`)
        }
      })
    }
    
    // Check custom claims object
    if (claimsFromToken.custom) {
      validRoles.forEach(role => {
        if (claimsFromToken.custom[role] === true) {
          if (!userRoles.includes(role)) {
            userRoles.push(role)
            console.log(`✅ Found role in custom claims: ${role}`)
          }
        }
      })
    }
    
    // Check roles array
    if (claimsFromToken.roles && Array.isArray(claimsFromToken.roles)) {
      claimsFromToken.roles.forEach(role => {
        if (validRoles.includes(role) && !userRoles.includes(role)) {
          userRoles.push(role)
          console.log(`✅ Found role in roles array: ${role}`)
        }
      })
    }
    
    // Store all roles for UI and localStorage
    console.log(`👤 User has ${userRoles.length} roles:`, userRoles)
    
    // Also store roles array in reactive ref for UI
    userRoles.forEach(role => {
      userClaims.value[role] = true
    })
    
    // Also update the single role property if found
    if (userRoles.length > 0) {
      userClaims.value.role = userRoles[0] // Use first role as primary
    }
    
    // Verify user has selected role
    const userHasSelectedRole = userRoles.includes(selectedRole.value)
    
    if (!userHasSelectedRole) {
      console.log(`❌ Role ${selectedRole.value} not found. Available roles: ${userRoles.join(', ')}`)
      
      // Clear the invalid role selection
      localStorage.removeItem("currentUserRole")
      
      throw new Error(`You don't have the "${selectedRole.value}" role. Your available roles are: ${userRoles.join(', ') || "None"}`)
    } else {
      console.log(`✅ Verified user has selected role: ${selectedRole.value}`)
    }
    
    // Store all user roles in localStorage for other components
    localStorage.setItem("userAllRoles", JSON.stringify(userRoles))

    // Step 3: Store user info immediately (no waiting for Cloud Functions)
    // Clear any stale data first
    localStorage.removeItem("currentUserRole")
    
    // Set new values
    localStorage.setItem("currentUserOrgName", selectedOrg.value)
    localStorage.setItem("currentUserRole", selectedRole.value)
    localStorage.setItem("userUID", user.uid)
    
    // Verify it was set correctly
    const verifyRole = localStorage.getItem("currentUserRole")
    console.log(`📝 Set localStorage.currentUserRole to: ${selectedRole.value}`)
    console.log(`✅ Verified localStorage.currentUserRole is: ${verifyRole}`)
    
    // Handle remember me
    if (rememberMe.value) {
      localStorage.setItem("email", email.value)
      localStorage.setItem("rememberedEmail", email.value)
      localStorage.setItem("rememberedPassword", password.value)
      localStorage.setItem("rememberedOrg", selectedRole.value)
    } else {
      localStorage.removeItem("email")
      localStorage.removeItem("rememberedEmail")
      localStorage.removeItem("rememberedPassword")
      localStorage.removeItem("rememberedOrg")
    }
    
    // Step 4: Show immediate success message
    showSuccessMessage("🔐 Login successful! Redirecting to dashboard...")
    
    // Step 5: Load user profile data, update LastLogin and ActiveRole
    try {
      console.log('👤 Loading user profile...')
      const userDocRef = doc(db, "users", user.uid)
      const snap = await getDoc(userDocRef)
      const userData = snap.exists() ? snap.data() : {}
      
      localStorage.setItem("currentUserNameFirst", userData?.NameFirst || "")
      localStorage.setItem("currentUserNameLast", userData?.NameLast || "")
      localStorage.setItem("tokenExpiration", idTokenResult.expirationTime)
      
      // Update LastLogin timestamp and ActiveRole to match user's selection
      await updateDoc(userDocRef, {
        LastLogin: serverTimestamp(),
        ActiveRole: selectedRole.value,
        ActiveOrg: selectedOrg.value
      })
      console.log(`✅ User profile loaded, LastLogin updated, ActiveRole set to: ${selectedRole.value}`)
      
    } catch (profileError) {
      console.warn("⚠️ Failed to load user profile:", profileError)
      // Continue anyway - this isn't critical
    }
    
    // Step 6: Route immediately
    const roleRoutes = {
      ADMIN: "/dashboard-admin",
      RefereeCoach: "/dashboard-refcoach", 
      Referee: "/dashboard-referee",
    };
    
    const dest = roleRoutes[selectedRole.value] || "/dashboard-referee"
    
    console.log(`🚀 Redirecting to: ${dest}`)
    
    setTimeout(() => {
      router.replace(dest)
    }, 800)
    
  } catch (loginErr) {
    console.error('❌ Login failed:', loginErr)
    
    // Handle specific Firebase auth errors
    if (loginErr.code === 'auth/too-many-requests') {
      loginError.value = "Too many failed attempts. Please try again later."
    } else if (loginErr.code === 'auth/user-not-found') {
      loginError.value = "No account found with this email address."
    } else if (loginErr.code === 'auth/wrong-password') {
      loginError.value = "Incorrect password. Please try again."
    } else if (loginErr.code === 'auth/email-not-verified') {
      loginError.value = "Please verify your email address."
      emailNotVerified.value = true
    } else {
      loginError.value = loginErr.message
    }
  } finally {
    loginLoading.value = false
  }
}

// Helper functions
const showSuccessMessage = (message) => {
  // Simple success message - could enhance with a snackbar
  console.log('✅', message)
}

onMounted(async () => {
  console.log("Organizations loaded:", orgOptions.value);
  
  // Auto-focus email field
  await nextTick()
  const emailField = document.querySelector('input[type="email"]')
  if (emailField) {
    emailField.focus()
  }
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body, html {
  height: 100%;
}

.page-background {
  background-image: url('../assets/2025-03-08_07-58-10.jpg');
  background-size: cover;
  background-position: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.info-container {
  text-align: left;
  color: white;
  max-width: 500px;
  height: 600px;
  padding: 20px;
  border: 2px solid white;
  border-radius: 10px;
  background-color: rgba(4, 4, 123, 0.648);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.mn-logo {
  width: 60px;
  height: auto;
  margin-bottom: 20px;
}

.info-container h2 {
  font-size: 60px;
  margin-bottom: 30px;
  text-align: center;
  font-weight: bold;
  width: 100%;
}

.MR-description {
  font-size: 22px;
  line-height: 1.5;
  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 40px;
  color: white;
}

.copywrite {
  font-size: 14px;
  font-style: italic;
  color: white !important;
  margin-left: 20px;
  margin-right: 20px;
  text-align: center;
  display: block;
  margin-bottom: 20px;
}

.layout-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 55%;
}

.form-container {
  background-color: rgba(255, 255, 255, 0.8);
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 400px;
  text-align: center;
}

.form-container h1 {
  margin-bottom: 20px;
}

.form-container input {
  height: 35px;
  width: 225px;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.checkbox_rememberme {
  display: inline-flex;
  align-items: center;
  margin-bottom: 35px;
}

.checkbox_rememberme input[type="checkbox"] {
  margin: 0;
  padding: 0;
  width: 16px;
  height: 16px;
}

.checkbox_rememberme label {
  margin: 0 0 0 6px;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1;
}

.form-container button {
  width: 100%;
  padding: 10px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
}

.form-container button:hover {
  background-color: #0056b3;
}

.form-container button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.newuser {
  margin-top: 25px;
}

.forgotpassword {
  margin-top: 10px;
}

.error-message {
  color: red;
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 15px;
}

.spinner {
  border: 2px solid transparent;
  border-top: 2px solid #fff;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Vuetify overrides */
:deep(.v-select) {
  margin-bottom: 15px;
}

:deep(.v-field) {
  border-radius: 5px;
}

:deep(.v-btn) {
  border-radius: 5px;
  text-transform: none;
}

:deep(.v-card) {
  border-radius: 10px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .layout-container {
    flex-direction: column;
    width: 90%;
    gap: 20px;
  }
  
  .info-container {
    max-width: 100%;
    height: auto;
  }
  
  .form-container {
    max-width: 100%;
  }
}
</style>